import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, Check, MessageSquare, ArrowLeft } from "lucide-react";
import { usePageContent, PageEditor } from "../components/shared/PageEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULTS = {
  bg_image: "https://customer-assets.emergentagent.com/job_cc551d47-83ea-4071-81f7-ae5cb0c54e08/artifacts/hxjihlox_IMG_4054.jpeg",
  hero_title: "KONTAKT & FIND OS",
  hero_subtitle: "Kom forbi eller send os en besked",
  address_name: "The Others MC Danmark",
  contact_person: "David Johnsen",
  street: "Marienbergvej 92",
  city: "4760 Vordingborg",
  country: "Danmark",
  phone: "+45 29 26 58 31",
  email: "kontakt@theothers.dk",
  map_embed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2295.8!2d11.9097!3d55.0077!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47ad5a7c6dc76c3d%3A0x6d8f6e1c6d7c8c8c!2sMarienbergvej%2092%2C%204760%20Vordingborg!5e0!3m2!1sda!2sdk!4v1234567890",
  lat: "55.0077",
  lng: "11.9097",
};

const SCHEMA = [
  { name: "bg_image", label: "Baggrundsbillede", type: "image" },
  { name: "hero_title", label: "Hovedtitel", type: "text" },
  { name: "hero_subtitle", label: "Undertekst", type: "text" },
  { name: "address_name", label: "Klubnavn", type: "text" },
  { name: "contact_person", label: "Kontaktperson", type: "text" },
  { name: "street", label: "Adresse (vej)", type: "text" },
  { name: "city", label: "Postnr & by", type: "text" },
  { name: "country", label: "Land", type: "text" },
  { name: "phone", label: "Telefon", type: "text" },
  { name: "email", label: "Email", type: "text" },
  { name: "lat", label: "Koordinat Latitude", type: "text", placeholder: "55.0077" },
  { name: "lng", label: "Koordinat Longitude", type: "text", placeholder: "11.9097" },
  { name: "map_embed", label: "Google Maps embed URL", type: "text" },
];

const ContactFindUsPage = () => {
  const navigate = useNavigate();
  const { content } = usePageContent("kontakt-find-os", DEFAULTS);
  const c = { ...DEFAULTS, ...content };
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const clubAddress = {
    name: c.address_name,
    contactPerson: c.contact_person,
    street: c.street,
    city: c.city,
    country: c.country,
    phone: c.phone,
    email: c.email,
    coordinates: { lat: parseFloat(c.lat) || 55.0077, lng: parseFloat(c.lng) || 11.9097 },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact-messages`, formData);
      setSubmitted(true);
      toast.success("Besked sendt!");
    } catch (err) {
      console.error("Error sending contact message:", err);
      toast.error("Kunne ikke sende besked. Prøv igen.");
    } finally {
      setSubmitting(false);
    }
  };

  const openDirections = () => {
    const { lat, lng } = clubAddress.coordinates;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-20 pb-12 px-4 flex items-center justify-center" data-testid="contact-find-us-page">
        <div className="mc-card p-8 text-center max-w-lg">
          <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-3xl font-['Oswald'] font-bold text-white mb-4">TAK FOR DIN BESKED</h2>
          <p className="text-gray-300 mb-6">
            Vi har modtaget din besked og vender tilbage snarest muligt.
          </p>
          <Link to="/" className="chrome-btn px-6 py-3 rounded-lg inline-block">
            Tilbage til forsiden
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pt-20 pb-12 px-4 relative" 
      data-testid="contact-find-us-page"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.4)), url('${c.bg_image}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <PageEditor pageKey="kontakt-find-os" schema={SCHEMA} defaults={DEFAULTS} />
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-all mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Tilbage
        </button>
        <h1 
          className="text-4xl sm:text-6xl font-['Oswald'] font-bold text-white mb-3 text-center"
          style={{
            textShadow: '3px 3px 6px rgba(0,0,0,0.9), 0 0 30px rgba(0,0,0,0.8), 0 0 60px rgba(0,0,0,0.6)'
          }}
        >
          {c.hero_title}
        </h1>
        <p 
          className="text-white text-xl text-center mb-10 font-medium"
          style={{
            textShadow: '2px 2px 4px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.8)'
          }}
        >
          {c.hero_subtitle}
        </p>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Address and Map */}
          <div className="space-y-6">
            {/* Address Card */}
            <div className="mc-card p-6 sm:p-8">
              <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-6 flex items-center gap-3">
                <MapPin className="w-6 h-6 text-blue-500" />
                FIND OS
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{clubAddress.name}</p>
                    <p className="text-gray-400">{clubAddress.street}</p>
                    <p className="text-gray-400">{clubAddress.city}</p>
                    <p className="text-gray-400">{clubAddress.country}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Kontaktperson</p>
                    <p className="text-white font-medium">{clubAddress.contactPerson}</p>
                    <a href={`tel:${clubAddress.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">
                      {clubAddress.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <a href={`mailto:${clubAddress.email}`} className="text-white hover:text-blue-400 transition-colors">
                      {clubAddress.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Get Directions Button */}
              <button
                onClick={openDirections}
                className="radial-btn w-full py-4 rounded-xl text-lg mt-6 flex items-center justify-center gap-3"
                data-testid="get-directions-btn"
              >
                <MapPin className="w-5 h-5" />
                Få rutevejledning
              </button>
            </div>

            {/* Map */}
            <div className="mc-card p-2 overflow-hidden">
              <div className="rounded-lg overflow-hidden">
                <iframe
                  title="The Others MC Location"
                  src={c.map_embed}
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="mc-card p-6 sm:p-8">
            <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-6 flex items-center gap-3">
              <MessageSquare className="w-6 h-6 text-blue-500" />
              SEND EN BESKED
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-gray-300 mb-2">Dit navn *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mc-input w-full"
                  required
                  placeholder="Skriv dit navn"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mc-input w-full"
                  required
                  placeholder="din@email.dk"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Emne *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="mc-input w-full"
                  required
                  placeholder="Hvad handler din besked om?"
                  data-testid="contact-subject-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Besked *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="mc-input w-full h-36 resize-none"
                  required
                  placeholder="Skriv din besked her..."
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="radial-btn w-full py-4 rounded-xl text-lg disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="submit-contact-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Sender...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Send besked
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};


export default ContactFindUsPage;
